import { Users, Award, Leaf, Heart } from "lucide-react";

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
            About ReptiCraft
          </h1>
          <p className="text-xl text-[#F4F1DE]/70 max-w-2xl mx-auto">
            Handcrafted reptile enclosures, made with passion and precision in the UK.
          </p>
        </div>

        {/* Story Section */}
        <div className="bg-[#121212] border border-white/10 p-8 md:p-12 mb-12">
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
            Our Story
          </h2>
          <div className="space-y-4 text-[#F4F1DE]/80 leading-relaxed">
            <p>
              ReptiCraft was born from a simple frustration: finding a quality reptile enclosure 
              that actually fits your space, your reptile's needs, and your style shouldn't be 
              so difficult.
            </p>
            <p>
              As reptile keepers ourselves, we spent years modifying off-the-shelf vivariums, 
              building makeshift enclosures, and settling for "good enough." In 2020, we decided 
              to do something about it. We started crafting bespoke enclosures in our workshop, 
              first for ourselves, then for friends, and eventually for the wider reptile-keeping 
              community.
            </p>
            <p>
              Today, ReptiCraft serves reptile enthusiasts across the UK and Europe. Every 
              enclosure we build is made to order, designed to your exact specifications, and 
              crafted with the same care we'd put into a home for our own scaly companions.
            </p>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-[#121212] border border-white/10 p-6">
            <Award className="w-10 h-10 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-3">
              Quality Craftsmanship
            </h3>
            <p className="text-[#F4F1DE]/70">
              We use only BS1088-certified marine plywood and toughened safety glass. 
              Every joint is precision-cut, every edge hand-finished. Our enclosures are 
              built to last for years.
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <Leaf className="w-10 h-10 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-3">
              Reptile-Safe Materials
            </h3>
            <p className="text-[#F4F1DE]/70">
              All our wood finishes use reptile-safe, water-based stains. No toxic fumes, 
              no harmful chemicals. We've carefully sourced every material to ensure it's 
              completely safe for your cold-blooded friends.
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <Users className="w-10 h-10 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-3">
              Made to Order
            </h3>
            <p className="text-[#F4F1DE]/70">
              No mass production here. Every enclosure is built specifically for you, 
              to your exact dimensions and specifications. Need a corner unit? A hexagonal 
              display piece? We've got you covered.
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <Heart className="w-10 h-10 text-[#E07A5F] mb-4" />
            <h3 className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-3">
              Keepers First
            </h3>
            <p className="text-[#F4F1DE]/70">
              We're reptile keepers too. We understand the importance of proper humidity, 
              ventilation, and heating. Every design decision is made with your animal's 
              welfare in mind.
            </p>
          </div>
        </div>

        {/* Workshop Section */}
        <div className="bg-[#121212] border border-white/10 p-8 md:p-12 mb-12">
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
            Our Workshop
          </h2>
          <div className="text-[#F4F1DE]/80 leading-relaxed space-y-4">
            <p>
              Based in [Location TBC], our workshop is where the magic happens. Each 
              enclosure passes through the hands of skilled craftspeople who take pride 
              in their work.
            </p>
            <p>
              From the initial cut of the marine plywood to the final application of 
              reptile-safe stain, we maintain strict quality control at every step. 
              We don't rush — your enclosure is ready when it's perfect, not before.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#E07A5F]/20 to-transparent border border-[#E07A5F]/30 p-8 rounded-lg">
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
            Ready to build your perfect enclosure?
          </h2>
          <p className="text-[#F4F1DE]/70 mb-6">
            Use our configurator to design your dream habitat, or get in touch if you 
            have something special in mind.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/configurator"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-bold uppercase tracking-wider transition-colors"
            >
              Start Building
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-white/20 text-[#F4F1DE] hover:border-white/40 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
